import { Assembly, box, blob, extrude, latheZ, rodZ, dome } from '../geometry.js';

/**
 * The combat knife — a fixed-blade melee weapon. No ammunition, no moving
 * parts: `tryFire()` in `src/weapons/index.js` branches on `def.class ===
 * 'melee'` and does a short raycast instead of spawning a projectile, so this
 * model only has to satisfy `Viewmodel.addWeapon()`'s node contract.
 *
 * Weapon-local origin follows the same convention as the guns: roughly the
 * web of the shooting hand, +z toward the shooter, -z toward the tip.
 */
export function buildKnife() {
  const bladeLen = 0.155;
  const handleLen = 0.115;
  const zGuard = 0.02; // guard sits just forward of the hand
  const zTip = zGuard - bladeLen;

  const body = new Assembly('knife-body');

  /* ---- blade ------------------------------------------------------------
   * Profile is the blade's SIDE silhouette (x = distance from guard, y =
   * height above/below centreline); `depth` is the blade THICKNESS. Adding it
   * with ry=+90deg turns local +x (the length axis) into world -z (forward),
   * so the profile's y stays world-up and the extrude depth becomes the
   * blade's left-right thickness — thin, as a blade should be. */
  const bladeProfile = [
    [0, 0.009],
    [bladeLen * 0.62, 0.008],
    [bladeLen * 0.86, 0.004],
    [bladeLen, 0],
    [bladeLen * 0.7, -0.008],
    [0, -0.008],
  ];
  const blade = extrude(bladeProfile, 0.0032, { bevel: 0.0004 });
  body.add(blade, 'steel_bright', { y: 0, z: zGuard, ry: Math.PI / 2 });
  blade.dispose();

  // A shallow fuller (blood groove) — a thin cavity-toned sliver just off the
  // spine, purely decorative.
  const fuller = extrude(
    [
      [0.01, 0.0035],
      [bladeLen * 0.55, 0.0025],
      [bladeLen * 0.8, 0],
      [bladeLen * 0.55, -0.0015],
      [0.01, -0.0015],
    ],
    0.0012,
    { bevel: 0.0002 }
  );
  body.add(fuller, 'steel_black', { y: 0, z: zGuard - 0.0008, ry: Math.PI / 2 });
  fuller.dispose();

  /* ---- guard -------------------------------------------------------------- */
  const guard = box(0.007, 0.026, 0.012, 0.0015, 2);
  body.add(guard, 'steel_black', { y: 0, z: zGuard + 0.006 });
  guard.dispose();

  /* ---- handle -------------------------------------------------------------
   * A ribbed rubber grip over a steel tang, with a small pommel ring so the
   * palm has something to key against. */
  const handle = rodZ(0.0105, 0.0095, handleLen, 14, 0.0006);
  body.add(handle, 'rubber', { y: 0, z: zGuard + 0.012 + handleLen / 2 });
  handle.dispose();
  for (let i = 0; i < 5; i++) {
    const rib = latheZ(
      [
        [0.0105, 0],
        [0.0125, 0.003],
        [0.0105, 0.006],
      ],
      16
    );
    body.add(rib, 'rubber', { y: 0, z: zGuard + 0.02 + i * (handleLen / 6) });
    rib.dispose();
  }
  const pommel = dome(0.012, 14, 0.75);
  body.add(pommel, 'steel_black', {
    y: 0,
    z: zGuard + 0.012 + handleLen,
    rx: Math.PI,
  });
  pommel.dispose();
  const pommelRing = blob(0.006, 0.006, 0.014, 0.0015, 2);
  body.add(pommelRing, 'steel_bright', {
    x: 0,
    y: -0.014,
    z: zGuard + 0.012 + handleLen - 0.004,
    rx: Math.PI / 2,
  });
  pommelRing.dispose();

  return {
    id: 'knife',
    label: 'Combat Knife',
    fxClass: 'melee',
    body,
    moving: {},
    nodes: {
      muzzle: [0, 0, zTip],
      eject: [0, 0.012, zGuard],
      sight: [0, 0.02, zGuard - bladeLen * 0.4],
      sightAxis: [0, 0, -1],
      ironSight: [0, 0.02, zGuard - bladeLen * 0.4],
      // A single-hand blade held in a forward, slightly raised guard — no
      // handguard cylinder to grip, so no `handguard` node (same as
      // pistol.js; `Viewmodel._fitSupportHand` skips gracefully without it).
      gripR: {
        pos: [0.012, -0.01, zGuard + 0.05],
        finger: [0.05, -0.45, -0.89],
        back: [0.98, 0.1, 0.05],
      },
      gripL: {
        pos: [-0.05, -0.06, 0.05],
        finger: [0.3, -0.5, -0.8],
        back: [-0.3, 0.6, -0.5],
      },
      magSeat: { pos: [0, 0, 0], rot: [0, 0, 0] },
    },
    shell: { caseLen: 0.02, rimR: 0.005 },
    magSize: { len: 0.1, w: 0.02, d: 0.02 },
  };
}
